import { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Sun, Moon } from "lucide-react";
import { NAV, COMPANY } from "../data/site";
import { useTheme } from "./ThemeProvider";
import { cn } from "../lib/utils";

export const Navbar = () => {
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={cn(
        "sticky top-0 z-50 w-full transition-[background-color,border-color,backdrop-filter] duration-300 backdrop-blur-xl",
        scrolled
          ? "bg-background/85 border-b border-border"
          : "bg-background/60 border-b border-transparent"
      )}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center group"
          >
            <span className="inline-flex items-center justify-center px-2 py-1 bg-white dark:bg-white rounded-sm">
              <img
                src="/brand/rns-logo-wide.png"
                alt="RNS Automobile"
                className="h-14 md:h-16 w-auto object-contain"
              />
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  cn(
                    "px-4 py-2 text-sm font-medium tracking-wide uppercase transition-colors duration-200",
                    "hover:text-[#C8102E]",
                    isActive
                      ? "text-[#C8102E]"
                      : "text-foreground/80"
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={`tel:${COMPANY.phoneLink}`}
              data-testid="nav-phone"
              className="hidden md:inline-block text-xs uppercase tracking-overline text-muted-foreground hover:text-foreground transition-colors duration-200"
            >
              {COMPANY.phone}
            </a>
            <button
              onClick={toggle}
              data-testid="theme-toggle"
              aria-label="Toggle theme"
              className="h-9 w-9 flex items-center justify-center border border-border hover:border-[#C8102E] transition-colors duration-200"
            >
              {theme === "dark" ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </button>
            <Link
              to="/contact"
              data-testid="nav-cta-enquire"
              className="hidden md:inline-flex items-center h-9 px-4 bg-[#C8102E] text-white text-xs uppercase tracking-overline font-bold hover:bg-[#A00D24] transition-colors duration-200"
            >
              Enquire
            </Link>
            <button
              className="lg:hidden h-9 w-9 flex items-center justify-center border border-border"
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-border bg-background/95 backdrop-blur-xl"
        >
          <div className="px-5 py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  cn(
                    "py-3 px-3 text-sm uppercase tracking-overline font-semibold border-l-2",
                    isActive
                      ? "border-[#C8102E] text-[#C8102E]"
                      : "border-transparent text-foreground/80"
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="mt-3 inline-flex items-center justify-center h-11 bg-[#C8102E] text-white text-xs uppercase tracking-overline font-bold"
            >
              Enquire Now
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
